import { Button } from '@/components/ui/button'
import { Check, CreditCard, DollarSign, Globe, Loader2, X } from 'lucide-react'
import { serviceChargeOverrideFromOrder, type Order, type OrderItem, type OnlineSubMethod, type ServiceChargeOverride } from '@/types'
import type { computeCartTotals } from '@/lib/counterPricing'
import { cn } from '@/lib/utils'
import { TendersLedger } from '@/components/counter/rail/TendersLedger'
import { TenderInput, type TenderIntent } from '@/components/counter/rail/TenderInput'
import { CounterLoyaltyChip } from '@/components/counter/CounterLoyaltyChip'
import {
  LineDiscountPopover,
  type LineDiscountValue,
} from '@/components/counter/rail/LineDiscountPopover'
import { DeliveryFeePopover } from '@/components/counter/rail/DeliveryFeePopover'
import { ServiceChargePopover } from '@/components/counter/rail/ServiceChargePopover'

export type CounterPaymentTotals = ReturnType<typeof computeCartTotals>

/**
 * Render a fractional rate like 0.075 as "7.5%" / 0.15 as "15%" — matches the
 * convention used on the printed receipt so the UI and the bill agree.
 */
function formatRatePct(fraction: number): string {
  if (!Number.isFinite(fraction) || fraction <= 0) return ''
  const rounded = Math.round(fraction * 10000) / 100
  return `${rounded}%`
}

export type CounterPaymentPanelProps = {
  payOrder: Order
  paymentCheckoutIntent: TenderIntent
  onPaymentIntent: (intent: TenderIntent) => void
  paymentOrderFetching: boolean
  billableItems: OrderItem[]
  paymentTotals: CounterPaymentTotals | null
  formatCurrency: (amount: number) => string
  payAmount: number
  discountMode: 'amount' | 'percent'
  onDiscountModeChange: (mode: 'amount' | 'percent') => void
  discountValue: string
  onDiscountValueChange: (value: string) => void
  discountMutationPending: boolean
  onApplyDiscount: () => void
  processPaymentPending: boolean
  onPrimaryPay: () => void
  /** Close a Rs 0 / fully comps check (no tender). */
  onCompleteZeroBalance?: () => void
  /**
   * Active online sub-methods (Easypaisa/JazzCash/…), migration 050. When the
   * Online tender is selected and this is non-empty, the panel shows a provider
   * picker inline and the primary CTA requires a pick. Empty = plain online flow.
   */
  onlineSubMethods?: OnlineSubMethod[]
  selectedOnlineSub?: string | null
  onSelectOnlineSub?: (slug: string) => void
  /**
   * F-MM-N23: the order is marketplace-settled. Tender is locked to Online and
   * the provider is fixed to the one named after the order type (created by
   * the backend when the type was marked). Cash/Card are greyed out; the
   * backend rejects them regardless.
   */
  marketplaceLock?: { label: string; providerSlug?: string }
  /**
   * Inline confirmation surfaced directly above the remaining-balance ledger
   * after a partial payment has been recorded. Replaces a floating toast that
   * would draw the operator's eye away from the payment rail.
   */
  partialPaymentBanner?: string | null
  onDismissPartialPaymentBanner?: () => void
  /** Provisional thermal bill (pre-payment); optional — counter checkout only. */
  onPrintProvisionalBill?: () => void
  printProvisionalDisabled?: boolean
  printProvisionalPending?: boolean
  /**
   * Called when the promo code changes (apply or remove) so the parent
   * can re-fetch the order — totals will shift.
   */
  onPromoChanged?: () => void
  /**
   * Server-built label of an applied bank card promo ("HBL Card Discount
   * (20%)"). When set, it replaces the generic "Discount" row label so the
   * bill totals name the bank deal the customer is getting.
   */
  cardPromoLabel?: string | null
  /**
   * Apply (or clear, when next is null) a per-line discount. The handler
   * is expected to forward straight to the server; pass undefined to hide
   * the per-line discount affordance entirely.
   */
  onApplyItemDiscount?: (itemId: string, next: LineDiscountValue | null) => void
  /** Disables the per-line discount popovers (e.g. promo applied). */
  lineDiscountDisabled?: boolean
  /** Hint shown over a disabled per-line discount trigger. */
  lineDiscountDisabledHint?: string
  /** Is a per-line discount mutation in flight (gates the Apply button). */
  lineDiscountPending?: boolean
  /**
   * Edit the per-order delivery fee (delivery orders only). Apply passes a
   * number; Reset passes null (revert to the settings default). Omit to hide
   * the editable affordance.
   */
  onEditDeliveryFee?: (next: number | null) => void
  /** Settings default delivery fee for the order type — the Reset target. */
  deliveryDefaultFee?: number
  /** Delivery-fee mutation in flight (gates Apply/Reset). */
  deliveryFeePending?: boolean
  /**
   * Edit the per-order service charge (any order type). Apply passes a
   * percent or fixed-amount override; Reset passes null (revert to the
   * settings rate). Omit to hide the editable affordance.
   */
  onEditServiceCharge?: (next: ServiceChargeOverride | null) => void
  /** Settings service rate for the order type (fraction) — the Reset target. */
  serviceChargeDefaultRate?: number
  /** Service-charge mutation in flight (gates Apply/Reset). */
  serviceChargePending?: boolean
  /**
   * F-MM-N24: this tenant may settle bills from a gift card balance. Shows an
   * "Apply gift card" action above the primary CTA; the dialog itself is owned
   * by the parent, alongside the other checkout modals.
   *
   * Deliberately NOT a fourth tender tile: the tiles pick the order's TAX
   * INTENT (cash | card | online), and a gift card carries no rate of its own.
   * Making it a tile would put it in the rate union and invite exactly the
   * kind of derived-rate coupling invariant #11 exists to forbid.
   */
  giftCardTenderEnabled?: boolean
  onApplyGiftCard?: () => void
  /** F-MM-N30: staff meal / customer tab. Hidden unless a kind is enabled. */
  houseAccountsEnabled?: boolean
  onChargeToAccount?: () => void
}

export function CounterPaymentPanel({
  payOrder,
  paymentCheckoutIntent,
  onPaymentIntent,
  paymentOrderFetching,
  billableItems,
  paymentTotals,
  formatCurrency,
  payAmount,
  discountMode,
  onDiscountModeChange,
  discountValue,
  onDiscountValueChange,
  discountMutationPending,
  onApplyDiscount,
  processPaymentPending,
  onPrimaryPay,
  onCompleteZeroBalance,
  onlineSubMethods,
  selectedOnlineSub,
  onSelectOnlineSub,
  marketplaceLock,
  partialPaymentBanner,
  onDismissPartialPaymentBanner,
  onPrintProvisionalBill,
  printProvisionalDisabled,
  printProvisionalPending,
  onPromoChanged,
  cardPromoLabel,
  onApplyItemDiscount,
  lineDiscountDisabled,
  lineDiscountDisabledHint,
  lineDiscountPending,
  onEditDeliveryFee,
  deliveryDefaultFee,
  deliveryFeePending,
  onEditServiceCharge,
  serviceChargeDefaultRate,
  serviceChargePending,
  giftCardTenderEnabled,
  onApplyGiftCard,
  houseAccountsEnabled,
  onChargeToAccount,
}: CounterPaymentPanelProps) {
  const svcOverride = serviceChargeOverrideFromOrder(payOrder)
  const payCtaClass =
    paymentCheckoutIntent === 'cash'
      ? 'bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500'
      : paymentCheckoutIntent === 'card'
        ? 'bg-sky-600 text-white hover:bg-sky-700 dark:bg-sky-600 dark:hover:bg-sky-500'
        : 'bg-violet-600 text-white hover:bg-violet-700 dark:bg-violet-600 dark:hover:bg-violet-500'

  // Online sub-method picker (migration 050). Shown inline when the Online
  // tender is selected and providers are configured. A pick is required before
  // the primary CTA enables.
  const showOnlineSubPicker =
    paymentCheckoutIntent === 'online' && (onlineSubMethods?.length ?? 0) > 0 && !marketplaceLock
  const selectedOnlineLabel = marketplaceLock
    ? marketplaceLock.label
    : onlineSubMethods?.find((m) => m.slug === selectedOnlineSub)?.label
  const onlineSubMissing = showOnlineSubPicker && !selectedOnlineSub

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border/80 bg-card p-3 shadow-lg ring-1 ring-black/[0.04] dark:border-border dark:ring-white/[0.08] sm:p-4">
      {/* Items charged */}
      <section aria-labelledby="items-charged-heading" className="space-y-2">
        <h3
          id="items-charged-heading"
          className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Items charged
        </h3>
        {billableItems.length === 0 ? (
          <p className="text-sm leading-relaxed text-muted-foreground">
            {paymentOrderFetching ? 'Loading line items…' : 'No billable line items.'}
          </p>
        ) : (
          <ul className="space-y-2 text-sm leading-snug">
            {billableItems.map((line) => {
              const lineSubtotal = line.unit_price * line.quantity
              const lineDiscount = line.discount_amount ?? 0
              const currentDiscount: LineDiscountValue | null =
                line.discount_percent != null
                  ? { mode: 'percent', value: line.discount_percent }
                  : lineDiscount > 0
                    ? { mode: 'amount', value: lineDiscount }
                    : null
              return (
                <li
                  key={line.id}
                  className="border-b border-border/60 pb-2 last:border-0 last:pb-0"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-foreground">
                        {line.product?.name ?? 'Item'}{' '}
                        <span className="font-normal text-muted-foreground">× {line.quantity}</span>
                      </div>
                      {/* Chosen options (F-MOD-N6) — the same '+' glyph the
                          printed bill uses, so screen and paper agree. */}
                      {((line.modifiers?.length ?? 0) > 0 || (line.combo_parts?.length ?? 0) > 0) && (
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {[
                            // F-DEAL-N1: a deal's fixed items lead, as on the bill.
                            ...(line.combo_parts ?? []),
                            ...(line.modifiers ?? []).map((m) => (m.quantity > 1 ? `${m.option_name} x${m.quantity}` : m.option_name)),
                          ]
                            .map((label) => `+ ${label}`)
                            .join('  ')}
                        </div>
                      )}
                      {line.price_rule_label ? (
                        <div className="mt-0.5 text-xs text-primary">{line.price_rule_label} price</div>
                      ) : null}
                      {line.special_instructions ? (
                        <div className="mt-0.5 text-xs italic text-muted-foreground">
                          {line.special_instructions}
                        </div>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {onApplyItemDiscount && (
                        <LineDiscountPopover
                          lineSubtotal={lineSubtotal}
                          current={currentDiscount}
                          disabled={lineDiscountDisabled}
                          disabledHint={lineDiscountDisabledHint}
                          pending={lineDiscountPending}
                          onApply={(next) => onApplyItemDiscount(line.id, next)}
                          onRemove={() => onApplyItemDiscount(line.id, null)}
                        />
                      )}
                      <div className="text-sm font-semibold tabular-nums text-foreground">
                        {formatCurrency(line.total_price)}
                      </div>
                    </div>
                  </div>
                  {lineDiscount > 0 && (
                    <div className="mt-1 flex justify-end">
                      <span className="text-[11px] tabular-nums text-amber-700 dark:text-amber-300">
                        Discount −{formatCurrency(lineDiscount)}
                      </span>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      {/* Bill totals */}
      <section aria-labelledby="bill-totals-heading" className="space-y-1.5 text-sm">
        <h3
          id="bill-totals-heading"
          className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
        >
          Bill totals
        </h3>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="font-medium tabular-nums">{formatCurrency(payOrder.subtotal)}</span>
        </div>
        {(payOrder.item_discounts_total ?? 0) > 0 && (
          <div className="flex justify-between gap-4 text-primary">
            <span>Item Discounts</span>
            <span className="font-medium tabular-nums">
              −{formatCurrency(payOrder.item_discounts_total ?? 0)}
            </span>
          </div>
        )}
        {payOrder.discount_amount > 0 && (
          <div className="flex justify-between gap-4 text-primary">
            <span>
              {cardPromoLabel ??
                payOrder.discount_label ??
                `Discount${
                  typeof payOrder.discount_percent === 'number' && payOrder.discount_percent > 0
                    ? ` (${formatRatePct(payOrder.discount_percent / 100)})`
                    : ''
                }`}
            </span>
            <span className="font-medium tabular-nums">−{formatCurrency(payOrder.discount_amount)}</span>
          </div>
        )}
        <div className="flex items-center justify-between gap-4">
          <span className="flex items-center gap-1.5 text-muted-foreground">
            Service Charges
            {paymentTotals?.serviceRate ? ` (${formatRatePct(paymentTotals.serviceRate)})` : ''}
            {svcOverride && (
              <span className="rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                {svcOverride.kind === 'amount' ? 'fixed' : 'custom'}
              </span>
            )}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="font-medium tabular-nums">
              {formatCurrency(payOrder.service_charge_amount ?? paymentTotals?.service ?? 0)}
            </span>
            {onEditServiceCharge && (
              <ServiceChargePopover
                currentAmount={payOrder.service_charge_amount ?? paymentTotals?.service ?? 0}
                currentRate={paymentTotals?.serviceRate ?? 0}
                override={svcOverride}
                defaultRate={serviceChargeDefaultRate ?? 0}
                onApply={(n) => onEditServiceCharge(n)}
                onReset={() => onEditServiceCharge(null)}
                pending={serviceChargePending}
              />
            )}
          </span>
        </div>
        <div className="flex justify-between gap-4">
          <span className="text-muted-foreground">
            Sales Tax
            {paymentTotals?.taxRate ? ` (${formatRatePct(paymentTotals.taxRate)})` : ''}
          </span>
          <span className="font-medium tabular-nums">{formatCurrency(payOrder.tax_amount)}</span>
        </div>
        {payOrder.order_type === 'delivery' && onEditDeliveryFee ? (
          <div className="flex items-center justify-between gap-4">
            <span className="flex items-center gap-1.5 text-muted-foreground">
              Delivery fee
              {payOrder.delivery_fee_override != null && (
                <span className="rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-800 dark:bg-amber-950/60 dark:text-amber-300">
                  custom
                </span>
              )}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="font-medium tabular-nums">
                {formatCurrency(payOrder.delivery_fee_amount ?? paymentTotals?.delivery ?? 0)}
              </span>
              <DeliveryFeePopover
                current={payOrder.delivery_fee_amount ?? 0}
                overridden={payOrder.delivery_fee_override != null}
                defaultFee={deliveryDefaultFee ?? 0}
                onApply={(n) => onEditDeliveryFee(n)}
                onReset={() => onEditDeliveryFee(null)}
                pending={deliveryFeePending}
              />
            </span>
          </div>
        ) : (
          (payOrder.delivery_fee_amount ?? 0) > 0 && (
            <div className="flex justify-between gap-4">
              <span className="text-muted-foreground">Delivery fee</span>
              <span className="font-medium tabular-nums">
                {formatCurrency(payOrder.delivery_fee_amount ?? paymentTotals?.delivery ?? 0)}
              </span>
            </div>
          )
        )}
        <div className="flex justify-between gap-4 border-t border-border pt-2 text-base font-bold tracking-tight">
          <span>Total</span>
          <span className="tabular-nums">{formatCurrency(payOrder.total_amount)}</span>
        </div>
      </section>

      {partialPaymentBanner ? (
        <div
          role="status"
          aria-live="polite"
          className="counter-fired-chip flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 shadow-sm dark:border-emerald-900/60 dark:bg-emerald-950/60 dark:text-emerald-100"
        >
          <Check className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1">
            <div className="font-semibold">Payment recorded</div>
            <div className="text-xs text-emerald-900/80 dark:text-emerald-200/80">
              {partialPaymentBanner}
            </div>
          </div>
          {onDismissPartialPaymentBanner && (
            <button
              type="button"
              onClick={onDismissPartialPaymentBanner}
              aria-label="Dismiss payment confirmation"
              className="shrink-0 rounded p-0.5 text-emerald-900/70 transition-colors hover:bg-emerald-100 hover:text-emerald-900 dark:text-emerald-200/70 dark:hover:bg-emerald-900/40 dark:hover:text-emerald-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ) : null}

      <TendersLedger
        payments={payOrder.payments}
        totalAmount={payOrder.total_amount}
        remaining={payAmount}
        formatCurrency={formatCurrency}
        guestLabelByPaymentId={
          // Split bill (F-FE-N20): label each tender with the guest it settled.
          payOrder.split_state
            ? Object.fromEntries(
                payOrder.split_state.guests
                  .filter((g) => g.paid_payment_id)
                  .map((g) => [g.paid_payment_id as string, g.label])
              )
            : undefined
        }
      />

      <CounterLoyaltyChip
        orderId={payOrder.id}
        customerId={payOrder.customer_id}
        onChanged={onPromoChanged}
      />

      <TenderInput
        intent={paymentCheckoutIntent}
        onIntent={onPaymentIntent}
        lockedIntent={marketplaceLock ? 'online' : undefined}
        lockedHint={
          marketplaceLock
            ? `${marketplaceLock.label} orders are paid by the platform — recorded as Online › ${marketplaceLock.label} only.`
            : undefined
        }
        discountMode={discountMode}
        onDiscountModeChange={onDiscountModeChange}
        discountValue={discountValue}
        onDiscountValueChange={onDiscountValueChange}
        discountPending={discountMutationPending}
        onApplyDiscount={onApplyDiscount}
        orderId={payOrder.id}
        onPromoChanged={onPromoChanged}
        hasExistingDiscount={(payOrder.discount_amount ?? 0) > 0}
        // F-MM-N24: the row renders iff this handler is passed, so all three
        // conditions are resolved here — feature on, something still owed, and
        // not a marketplace order (the platform settles those, not a guest).
        onApplyGiftCard={
          giftCardTenderEnabled &&
          onApplyGiftCard &&
          !marketplaceLock &&
          payAmount > 0 &&
          payOrder.status !== 'completed' &&
          !processPaymentPending &&
          // A split bill settles guest by guest, and every tender on one is
          // stamped with the guest it paid for. A gift leg carries no guest,
          // so it would land as an unlabelled payment and leave the split's
          // "N of M paid" progress lying. Pay the card off before splitting.
          !payOrder.split_state
            ? onApplyGiftCard
            : undefined
        }
        // F-MM-N30: whole bill only — the server refuses a bill that already
        // carries any payment (house_account_whole_bill_only), so the row is
        // offered only while nothing has been paid yet.
        onChargeToAccount={
          houseAccountsEnabled &&
          onChargeToAccount &&
          !marketplaceLock &&
          payOrder.status !== 'completed' &&
          !processPaymentPending &&
          !payOrder.split_state &&
          !(payOrder.payments ?? []).some((p) => (p.amount ?? 0) > 0)
            ? onChargeToAccount
            : undefined
        }
      />

      {marketplaceLock ? (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-muted-foreground">Online provider</div>
          <div className="flex h-11 items-center justify-center rounded-lg border border-violet-500 bg-violet-600 text-sm font-semibold text-white shadow-sm">
            <Check className="mr-1.5 h-4 w-4 shrink-0" />
            {marketplaceLock.label}
          </div>
        </div>
      ) : null}
      {showOnlineSubPicker ? (
        <div className="space-y-1.5">
          <div className="text-xs font-medium text-muted-foreground">
            Online provider
          </div>
          <div className="grid grid-cols-2 gap-2">
            {onlineSubMethods!.map((m) => {
              const selected = selectedOnlineSub === m.slug
              return (
                <button
                  key={m.slug}
                  type="button"
                  onClick={() => onSelectOnlineSub?.(m.slug)}
                  className={cn(
                    'flex h-11 items-center justify-center rounded-lg border text-sm font-semibold transition-colors',
                    selected
                      ? 'border-violet-500 bg-violet-600 text-white shadow-sm'
                      : 'border-border bg-card text-foreground hover:bg-violet-50 dark:hover:bg-violet-950/30'
                  )}
                >
                  {selected ? <Check className="mr-1.5 h-4 w-4 shrink-0" /> : null}
                  {m.label}
                </button>
              )
            })}
          </div>
        </div>
      ) : null}

      {onPrintProvisionalBill ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 w-full border-amber-300 bg-amber-50 text-sm font-medium text-amber-900 hover:bg-amber-100 hover:text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/50 dark:hover:text-amber-50"
          disabled={Boolean(printProvisionalDisabled) || Boolean(printProvisionalPending)}
          onClick={onPrintProvisionalBill}
        >
          {printProvisionalPending ? (
            <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" aria-hidden />
          ) : null}
          Print initial bill
        </Button>
      ) : null}

      {payAmount <= 0 && onCompleteZeroBalance ? (
        <Button
          type="button"
          className="h-12 w-full bg-emerald-600 text-base font-semibold text-white shadow-sm hover:bg-emerald-700 sm:h-11 sm:text-[15px]"
          disabled={processPaymentPending || payOrder.status === 'completed'}
          onClick={onCompleteZeroBalance}
        >
          {processPaymentPending ? (
            <Loader2 className="mr-2 h-4 w-4 shrink-0 animate-spin" aria-hidden />
          ) : (
            <Check className="mr-2 h-4 w-4 shrink-0" aria-hidden />
          )}
          Complete order — Rs 0
        </Button>
      ) : (
      <Button
        type="button"
        className={cn(
          'h-12 w-full text-base font-semibold shadow-sm transition-colors sm:h-11 sm:text-[15px]',
          payCtaClass
        )}
        disabled={payAmount <= 0 || processPaymentPending || onlineSubMissing || (marketplaceLock != null && paymentCheckoutIntent !== 'online')}
        onClick={onPrimaryPay}
      >
        {paymentCheckoutIntent === 'cash' && (
          <>
            <DollarSign className="mr-2 h-4 w-4 shrink-0" />
            Charge {formatCurrency(payAmount)} — Cash
          </>
        )}
        {paymentCheckoutIntent === 'card' && (
          <>
            <CreditCard className="mr-2 h-4 w-4 shrink-0" />
            Charge {formatCurrency(payAmount)} — Card
          </>
        )}
        {paymentCheckoutIntent === 'online' && (
          <>
            <Globe className="mr-2 h-4 w-4 shrink-0" />
            {showOnlineSubPicker
              ? selectedOnlineSub
                ? `Charge ${formatCurrency(payAmount)} — ${selectedOnlineLabel}`
                : 'Select an online provider'
              : `Charge ${formatCurrency(payAmount)} — Online`}
          </>
        )}
      </Button>
      )}
    </div>
  )
}
